import { angle, approxBetween, EPSILON, type Vec2, vec } from '../math';

import {
  type Arc,
  type Bounds,
  type Circle,
  type Ellipse,
  type Polygon,
  type Segment,
} from './types';

/**
 * Axis-aligned bounding boxes.
 *
 * Bounds are load-bearing well beyond drawing: the R-tree spatial index is built on
 * them, dirty-rectangle rendering unions them, and hit-testing uses them as a cheap
 * reject before exact geometry. Loose bounds are therefore a performance bug, not a
 * cosmetic one — which is why the arc and ellipse cases below compute tight boxes
 * rather than the easy conservative ones.
 */

/** Bounds that contain nothing. Unioning with this is a no-op, so it seeds accumulations. */
export const EMPTY: Bounds = Object.freeze({
  minX: Infinity,
  minY: Infinity,
  maxX: -Infinity,
  maxY: -Infinity,
});

export function bounds(minX: number, minY: number, maxX: number, maxY: number): Bounds {
  return { minX, minY, maxX, maxY };
}

export function isEmpty(b: Bounds): boolean {
  return b.minX > b.maxX || b.minY > b.maxY;
}

export function width(b: Bounds): number {
  return Math.max(0, b.maxX - b.minX);
}

export function height(b: Bounds): number {
  return Math.max(0, b.maxY - b.minY);
}

export function area(b: Bounds): number {
  return width(b) * height(b);
}

export function center(b: Bounds): Vec2 {
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
}

export function fromPoint(p: Vec2): Bounds {
  return { minX: p.x, minY: p.y, maxX: p.x, maxY: p.y };
}

export function fromPoints(points: Iterable<Vec2>): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

export function union(a: Bounds, b: Bounds): Bounds {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** Returns `EMPTY` when the boxes do not overlap. */
export function intersection(a: Bounds, b: Bounds): Bounds {
  const r = {
    minX: Math.max(a.minX, b.minX),
    minY: Math.max(a.minY, b.minY),
    maxX: Math.min(a.maxX, b.maxX),
    maxY: Math.min(a.maxY, b.maxY),
  };
  return isEmpty(r) ? EMPTY : r;
}

/** Grows the box by `amount` on every side. Negative values shrink it. */
export function expand(b: Bounds, amount: number): Bounds {
  return {
    minX: b.minX - amount,
    minY: b.minY - amount,
    maxX: b.maxX + amount,
    maxY: b.maxY + amount,
  };
}

export function containsPoint(b: Bounds, p: Vec2, epsilon: number = EPSILON): boolean {
  return approxBetween(p.x, b.minX, b.maxX, epsilon) && approxBetween(p.y, b.minY, b.maxY, epsilon);
}

export function containsBounds(outer: Bounds, inner: Bounds, epsilon: number = EPSILON): boolean {
  return (
    inner.minX >= outer.minX - epsilon &&
    inner.minY >= outer.minY - epsilon &&
    inner.maxX <= outer.maxX + epsilon &&
    inner.maxY <= outer.maxY + epsilon
  );
}

/** Overlap test. Touching boxes count as intersecting. */
export function intersects(a: Bounds, b: Bounds, epsilon: number = EPSILON): boolean {
  return (
    a.minX <= b.maxX + epsilon &&
    a.maxX >= b.minX - epsilon &&
    a.minY <= b.maxY + epsilon &&
    a.maxY >= b.minY - epsilon
  );
}

/* --------------------------------------------------------------- shape bounds */

export function ofSegment(s: Segment): Bounds {
  return {
    minX: Math.min(s.a.x, s.b.x),
    minY: Math.min(s.a.y, s.b.y),
    maxX: Math.max(s.a.x, s.b.x),
    maxY: Math.max(s.a.y, s.b.y),
  };
}

export function ofCircle(c: Circle): Bounds {
  return {
    minX: c.center.x - c.radius,
    minY: c.center.y - c.radius,
    maxX: c.center.x + c.radius,
    maxY: c.center.y + c.radius,
  };
}

export function ofPolygon(p: Polygon): Bounds {
  return fromPoints(p);
}

/**
 * Tight bounds for an arc.
 *
 * The naive approach — the bounding box of the two endpoints — is wrong (it misses the
 * bulge), and the box of the whole circle is correct but loose enough to hurt the
 * spatial index. The extremes of a circular arc are its endpoints plus whichever of the
 * four axis extreme points (0, π/2, π, 3π/2) the arc actually passes through.
 */
export function ofArc(a: Arc): Bounds {
  const start = vec.addScaled(a.center, vec.fromAngle(a.startAngle), a.radius);
  const end = vec.addScaled(a.center, vec.fromAngle(a.endAngle), a.radius);

  let minX = Math.min(start.x, end.x);
  let minY = Math.min(start.y, end.y);
  let maxX = Math.max(start.x, end.x);
  let maxY = Math.max(start.y, end.y);

  for (let q = 0; q < 4; q++) {
    const theta = q * angle.HALF_PI;
    if (!angle.isOnArc(theta, a.startAngle, a.endAngle, a.counterclockwise)) continue;
    const x = a.center.x + Math.cos(theta) * a.radius;
    const y = a.center.y + Math.sin(theta) * a.radius;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }

  return { minX, minY, maxX, maxY };
}

/**
 * Tight bounds for a rotated ellipse.
 *
 * Derived analytically rather than by sampling. For the parametrisation
 * `x(t) = cx + rx·cos t·cos φ − ry·sin t·sin φ`, setting `dx/dt = 0` gives
 * `tan t = −ry·tan φ / rx`, and substituting back yields the half-extent
 * `√((rx·cos φ)² + (ry·sin φ)²)`. The y half-extent follows symmetrically.
 */
export function ofEllipse(e: Ellipse): Bounds {
  const cos = Math.cos(e.rotation);
  const sin = Math.sin(e.rotation);
  const halfWidth = Math.hypot(e.rx * cos, e.ry * sin);
  const halfHeight = Math.hypot(e.rx * sin, e.ry * cos);
  return {
    minX: e.center.x - halfWidth,
    minY: e.center.y - halfHeight,
    maxX: e.center.x + halfWidth,
    maxY: e.center.y + halfHeight,
  };
}
